import { CityObj, Course, Qualification, SkillObj } from "../config/metadata";
import { CreateJobPostRequest, EditJobPostRequest, JobType, WorkMode, QualificationRequest, PostSkillsRequest, QualificationCustomResponse } from "../types/api";
import { JobFormDataType, QualificationUIType } from "../types/components";
import allSkills from "../config/allSkill.json";
import allCities from "../config/allCities.json";
import qualificationsData from '../config/allQualifications.json';


export const mapJobFormToApi = (prefilledFormData: JobFormDataType | null, formData: JobFormDataType, userId: string, postId: number): Partial<EditJobPostRequest> => {
  const updatedFields: Partial<EditJobPostRequest> = {
    postId: postId,
    modifiedBy: userId,
  };

  // Helper to check if a value is different from its default
  const isChanged = (currentValue: any, defaultValue: any) => {
    // for now send every key in edit
    // add patch support to send selective keys TODO
    return true;
    if (Array.isArray(currentValue) && Array.isArray(defaultValue)) {
      return currentValue.length !== defaultValue.length || !currentValue.every((val, i) => val === defaultValue[i]);
    }
    return currentValue !== defaultValue;
  };

  if (isChanged(formData.job_title, prefilledFormData?.job_title || '')) updatedFields.jobTitle = formData.job_title;
  if (isChanged(formData.job_description, prefilledFormData?.job_description || '')) updatedFields.jobDescription = formData.job_description;
  if (isChanged(formData.job_type, prefilledFormData?.job_type || '')) updatedFields.jobType = mapJobTypeToApi(formData.job_type);
  if (isChanged(formData.work_mode, prefilledFormData?.work_mode || '')) updatedFields.workMode = mapWorkModeToApi(formData.work_mode);
  if (isChanged(formData.min_salary, prefilledFormData?.min_salary || '')) updatedFields.minSalary = parseFloat(formData.min_salary);
  if (isChanged(formData.max_salary, prefilledFormData?.max_salary || '')) updatedFields.maxSalary = parseFloat(formData.max_salary);
  if (isChanged(formData.min_experience, prefilledFormData?.min_experience || '')) updatedFields.minExperience = parseInt(formData.min_experience);
  if (isChanged(formData.no_of_openings, prefilledFormData?.no_of_openings || 1)) updatedFields.noOfOpenings = formData.no_of_openings;
  if (isChanged(formData.expiration_dt, prefilledFormData?.expiration_dt || '')) updatedFields.expirationDt = formData.expiration_dt;
  if (isChanged(formData.scheduled_on, prefilledFormData?.scheduled_on || '')) updatedFields.scheduledOn = formData.scheduled_on;
  if (isChanged(formData.post_type, prefilledFormData?.post_type || '')) updatedFields.postType = formData.post_type;
  if (isChanged(formData.is_draft, prefilledFormData?.is_draft || false)) updatedFields.isDraft = formData.is_draft;
  if (isChanged(formData.post_status, prefilledFormData?.post_status || 'Draft')) updatedFields.postStatus = formData.post_status;
  // if (isChanged(formData.is_active, prefilledFormData?.is_active || true)) updatedFields.isActive = formData.is_active;
  if (isChanged(formData.skills_json, prefilledFormData?.skills_json || [])) {
    updatedFields.skills = formData.skills_json.map((skill) => {
      return {
        skillPriority: 'Required',
        skillId: skill.skillId,
      };
    });
  }
  if (isChanged(formData.nice_to_have_skills, prefilledFormData?.nice_to_have_skills || [])) {
    const niceToHaveSkills : PostSkillsRequest[] = formData.nice_to_have_skills.map((skill) => {
      return {
        skillPriority: 'Preferred',
        skillId: skill.skillId,
      };
    });
    updatedFields.skills = updatedFields.skills ? [...updatedFields.skills, ...niceToHaveSkills] : undefined;
  }
  if (isChanged(formData.selected_locations, prefilledFormData?.selected_locations || [])) updatedFields.locationIds = formData.selected_locations.map(a => a.cityId);
  if (isChanged(formData.selected_qualifications, prefilledFormData?.selected_qualifications || [])) updatedFields.qualifications = mapQualificationsToApi(formData.selected_qualifications);
  if (isChanged(formData.salary_currency, prefilledFormData?.salary_currency || 'INR')) updatedFields.salaryCurrency = formData.salary_currency
  // attachmentIds are handled separately, so no direct mapping here unless changed
  // if (isChanged(formData.jobAttachment, null)) updatedFields.attachmentIds = []; // Needs actual attachment ID logic

  // Special handling for salaryCurrency as it has a default mapping
  return updatedFields;
};

const mapJobTypeToApi = (jobType: string): JobType => {
  switch (jobType) {
    case 'FullTime': return "FullTime";
    case 'PartTime': return "PartTime";
    case 'Contract': return "Contract";
    case 'Internship': return "Internship";
    case 'Temporary': return "Temporary";
    default: return "FullTime"; // Default or handle error
  }
};

const mapWorkModeToApi = (workMode: string): WorkMode => {
  switch (workMode) {
    case 'Remote': return "Remote";
    case 'Hybrid': return "Hybrid";
    case 'OnSite': return "OnSite";
    default: return "OnSite"; // Default or handle error
  }
};

const mapQualificationsToApi = (
  qualifications: QualificationUIType[]
): QualificationRequest[] => {
  // This mapping is complex as JobForm.tsx stores qualifications as strings like "B.Tech - Computer Science"
  // and API expects QualificationRequest[] = { courseId: number, customCourseName?: string }
  // For now, we'll assume that if a qualification string contains " - ", it's "Qualification - Course"
  // and we'll try to extract them. A more robust solution would involve looking up actual IDs.
  return qualifications.map((q) => {
    return {
      qualificationId: q.qualificationId,
      courseId: q.course.courseId,
      customCourseName: q.customInput,
    };
  });
};

// Api Data >> UI mapp
const allSkillNames: SkillObj[] = allSkills.skills

export const mapskills = (skills : PostSkillsRequest[], skillType : PostSkillsRequest['skillPriority'] ): SkillObj[] => {
  const result : SkillObj[] = []
  skills.filter(s => s.skillPriority == skillType).map(s => allSkillNames.find(a => a.skillId == s.skillId)).forEach(skillObj => {
    if (skillObj != undefined || skillObj != null) result.push(skillObj)
  })
  return result;
}
export const mapLocations = (locationIds?: number[]): CityObj[] => {
  const result : CityObj[] = []
  if (!locationIds) return result;
  locationIds.map(s => mapLocation(s)).forEach(cityObj => {
    if (cityObj != undefined || cityObj != null) result.push(cityObj)
  })
  return result;
}

export const mapLocation = (locationId: number): CityObj | undefined => {
  return allCities.find(a => a.cityId == locationId)
}

export const mapQualifcations = (
  courses?: QualificationCustomResponse[]
): QualificationUIType[] => {
  courses?.map((course) => {});
  return [];
};

export const resolveQualificationName = (
  qid: number | null
): Qualification | undefined => {
  if (!qid) return;
  return qualificationsData.find((q: any) => q.qualificationId === qid);
};

export const resolveCourseName = (
  qid: number | null,
  cid: number | null
): Course | undefined => {
  if (qid && cid) {
    const courses =
      qualificationsData.find((q) => q.qualificationId === qid)?.courses ||
      [];
    return courses.find((c) => c.courseId === cid);
  } else if (cid) {
    let match: Course | undefined;
    for (let i = 0; i < qualificationsData.length; i++) {
      const q = qualificationsData[i];
      match = q.courses.find((c: any) => c.courseId === cid);
      if (match) {
        return match;
      }
    }
  }
};
export const mapApiCourseToQualification = (
  course: QualificationCustomResponse
): QualificationUIType | undefined => {
  let match: Course | undefined;
  console.log(course);
  for (let i = 0; i < qualificationsData.length; i++) {
    const q = qualificationsData[i];
    match = q.courses.find((c) => c.courseId === course.courseId);
    if (match) {
      return {
        qualificationId: q.qualificationId,
        qualificationName: q.qualificationName,
        qualificationType: q.qualificationType,
        course: match,
        customInput: course.customCourseName,
      };
    }
  }
  return;
};
